import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  createSourcePermissionDecision,
  type SourcePermissionDecision,
  type SourcePermissionScope,
  type SourcePermissionStatus,
  type SourceWatchItem,
  type SourceWatchSource,
} from './api';

type Props = {
  busy: boolean;
  csrfToken: string;
  decisions: SourcePermissionDecision[];
  items: SourceWatchItem[];
  onRun: (action: () => Promise<void>, message: string) => Promise<void>;
  sources: SourceWatchSource[];
};

const scopes = [
  ['FACTS_AND_PARAPHRASE', '事実・安全ポイントの独自再構成'],
  ['SHORT_QUOTE', '必要最小限の短文引用'],
  ['PDF_PAGE', 'PDFページ'],
  ['TABLE_OR_CHART', '表・グラフ'],
  ['ILLUSTRATION', 'イラスト'],
  ['PHOTO', '写真'],
  ['VIDEO', '動画'],
  ['LAYOUT', '第三者レイアウト'],
] as const satisfies ReadonlyArray<readonly [SourcePermissionScope, string]>;

const statuses = [
  ['UNKNOWN', '未確認'],
  ['NOT_REQUIRED_CONFIRMED', '許諾不要を確認済み'],
  ['PERMITTED', '許諾済み'],
  ['REQUEST_REQUIRED', '許諾申請が必要'],
  ['REQUESTED', '許諾申請中'],
  ['EXPIRED', '期限切れ'],
  ['PROHIBITED', '利用禁止'],
] as const satisfies ReadonlyArray<readonly [SourcePermissionStatus, string]>;

function statusLabel(status: SourcePermissionStatus): string {
  return statuses.find(([value]) => value === status)?.[1] ?? status;
}

function statusClass(decision: SourcePermissionDecision | undefined): string {
  if (decision?.allowsUse) return 'approved';
  if (decision?.effectiveStatus === 'PROHIBITED') return 'rejected';
  if (
    decision?.effectiveStatus === 'REQUESTED' ||
    decision?.effectiveStatus === 'REQUEST_REQUIRED'
  ) {
    return 'in_review';
  }
  return 'warning';
}

function itemPermissionSummary(
  itemId: string,
  currentByKey: Map<string, SourcePermissionDecision>,
): { allowed: number; blocked: number; unknown: number } {
  let allowed = 0;
  let blocked = 0;
  let unknown = 0;
  for (const [scope] of scopes) {
    const decision = currentByKey.get(`${itemId}:${scope}`);
    if (!decision) unknown += 1;
    else if (decision.allowsUse) allowed += 1;
    else blocked += 1;
  }
  return { allowed, blocked, unknown };
}

export function SourcePermissionPanel({
  busy,
  csrfToken,
  decisions,
  items,
  onRun,
  sources,
}: Props) {
  const reviewableItems = useMemo(
    () => items.filter((item) => item.administratorStatus !== 'REJECTED'),
    [items],
  );
  const [selectedId, setSelectedId] = useState<string | null>(reviewableItems[0]?.id ?? null);
  const [selectedScope, setSelectedScope] = useState<SourcePermissionScope>('FACTS_AND_PARAPHRASE');
  const [query, setQuery] = useState('');
  const sourcesById = useMemo(
    () => new Map(sources.map((source) => [source.id, source])),
    [sources],
  );
  const currentByKey = useMemo(
    () =>
      new Map(
        decisions
          .filter((decision) => decision.isCurrent)
          .map((decision) => [`${decision.itemId}:${decision.scope}`, decision]),
      ),
    [decisions],
  );
  const filteredItems = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ja-JP');
    return reviewableItems.filter((item) =>
      needle
        ? [item.rawTitle, item.sourceTitle, item.citationLabel]
            .filter((value): value is string => value !== null)
            .some((value) => value.toLocaleLowerCase('ja-JP').includes(needle))
        : true,
    );
  }, [query, reviewableItems]);

  useEffect(() => {
    if (selectedId === null || !filteredItems.some((item) => item.id === selectedId)) {
      setSelectedId(filteredItems[0]?.id ?? null);
    }
  }, [filteredItems, selectedId]);

  const selected = reviewableItems.find((item) => item.id === selectedId) ?? null;
  const selectedSource = selected ? sourcesById.get(selected.sourceId) : undefined;
  const currentDecision = selected
    ? currentByKey.get(`${selected.id}:${selectedScope}`)
    : undefined;
  const history = selected
    ? decisions.filter(
        (decision) => decision.itemId === selected.id && decision.scope === selectedScope,
      )
    : [];
  const currentDecisions = decisions.filter((decision) => decision.isCurrent);
  const metrics = {
    allowed: currentDecisions.filter((decision) => decision.allowsUse).length,
    expired: currentDecisions.filter((decision) => decision.effectiveStatus === 'EXPIRED').length,
    prohibited: currentDecisions.filter((decision) => decision.effectiveStatus === 'PROHIBITED')
      .length,
    requested: currentDecisions.filter((decision) =>
      ['REQUESTED', 'REQUEST_REQUIRED'].includes(decision.effectiveStatus),
    ).length,
    unknownItems: reviewableItems.filter(
      (item) => !currentByKey.has(`${item.id}:FACTS_AND_PARAPHRASE`),
    ).length,
  };

  async function submitDecision(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selected === null) return;
    const data = new FormData(event.currentTarget);
    const value = (name: string): string => {
      const field = data.get(name);
      return typeof field === 'string' ? field.trim() : '';
    };
    const status = value('status') as SourcePermissionStatus;
    await onRun(async () => {
      await createSourcePermissionDecision(
        selected.id,
        {
          effectiveFrom: value('effectiveFrom') || null,
          evidenceNote: value('evidenceNote'),
          evidenceUrl: value('evidenceUrl'),
          scope: selectedScope,
          status,
          supersedesDecisionId: currentDecision?.id ?? null,
          validUntil: value('validUntil') || null,
        },
        csrfToken,
      );
    }, '利用用途の許諾判断を履歴として登録しました。');
  }

  return (
    <section className="stack source-permission-panel" aria-labelledby="source-permission-heading">
      <div className="section-heading">
        <div>
          <h3 id="source-permission-heading">利用許諾・教材候補</h3>
          <p className="muted">
            事実の独自再構成と第三者コンテンツの転載を分け、用途ごとに根拠を記録します。判断は上書き・削除せず、新しい履歴で置き換えます。
          </p>
        </div>
      </div>
      <div className="notice warning">
        Source
        Watchから画像、PDF、動画、表、イラストを自動取得・転載しません。許諾がない用途は有料教材へ組み込めません。
      </div>
      <div className="source-permission-metrics">
        <div>
          <strong>{metrics.unknownItems}</strong>
          <span>事実利用の判断なし</span>
        </div>
        <div>
          <strong>{metrics.allowed}</strong>
          <span>利用可能な用途</span>
        </div>
        <div>
          <strong>{metrics.requested}</strong>
          <span>許諾確認・申請中</span>
        </div>
        <div>
          <strong>{metrics.expired}</strong>
          <span>期限切れ</span>
        </div>
        <div>
          <strong>{metrics.prohibited}</strong>
          <span>利用禁止</span>
        </div>
      </div>

      {reviewableItems.length === 0 ? (
        <div className="card empty-state">許諾を判断する検知情報はまだありません。</div>
      ) : (
        <div className="source-permission-workspace">
          <div className="card source-permission-items">
            <label className="source-permission-search">
              情報を検索
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="タイトル・発行元"
                value={query}
              />
            </label>
            {filteredItems.map((item) => {
              const summary = itemPermissionSummary(item.id, currentByKey);
              return (
                <button
                  className={item.id === selectedId ? 'active' : ''}
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  type="button"
                >
                  <strong>{item.rawTitle}</strong>
                  <small>{item.sourceTitle}</small>
                  <small>
                    利用可 {summary.allowed} / 要確認 {summary.unknown} / 利用不可 {summary.blocked}
                  </small>
                </button>
              );
            })}
          </div>

          <div className="card stack source-permission-detail">
            {selected === null ? (
              <div className="empty-state">左の一覧から情報を選択してください。</div>
            ) : (
              <>
                <div>
                  <h3>{selected.rawTitle}</h3>
                  <p className="muted">
                    {selected.sourceTitle} · {selected.category}
                  </p>
                  <small>
                    source policy: {selectedSource?.contentUsagePolicy ?? '未設定'} / 商用利用{' '}
                    {selectedSource?.commercialUseStatus ?? '未設定'}
                  </small>
                </div>
                {selectedSource?.contentUsagePolicy === 'REFERENCE_ONLY' && (
                  <div className="notice error">
                    このSourceはREFERENCE_ONLYです。研究参考以外の用途を利用可にできません。
                  </div>
                )}
                <a
                  href={selected.canonicalItemUrl ?? selected.sourceUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  原典を別タブで確認
                </a>

                <div className="source-permission-matrix" role="list" aria-label="用途別の許諾判断">
                  {scopes.map(([scope, label]) => {
                    const decision = currentByKey.get(`${selected.id}:${scope}`);
                    return (
                      <button
                        className={scope === selectedScope ? 'active' : ''}
                        key={scope}
                        onClick={() => setSelectedScope(scope)}
                        role="listitem"
                        type="button"
                      >
                        <span>{label}</span>
                        <span className={`status-chip ${statusClass(decision)}`}>
                          {decision ? statusLabel(decision.effectiveStatus) : '判断なし'}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <form
                  className="stack source-permission-form"
                  key={`${selected.id}:${selectedScope}:${currentDecision?.id ?? 'new'}`}
                  onSubmit={(event) => void submitDecision(event)}
                >
                  <h4>{scopes.find(([scope]) => scope === selectedScope)?.[1]}</h4>
                  {currentDecision && (
                    <div className="notice">
                      現在: {statusLabel(currentDecision.effectiveStatus)} · 判断日時{' '}
                      {currentDecision.decidedAt}
                      <br />
                      新しい判断はこの記録を置き換えます。過去記録は削除されません。
                    </div>
                  )}
                  <label>
                    判断
                    <select defaultValue={currentDecision?.status ?? 'UNKNOWN'} name="status">
                      {statuses.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    根拠URL（公開HTTPS）
                    <input
                      defaultValue={
                        currentDecision?.evidenceUrl ??
                        selected.canonicalItemUrl ??
                        selected.sourceUrl
                      }
                      name="evidenceUrl"
                      required
                      type="url"
                    />
                  </label>
                  <label>
                    判断根拠・条件（10文字以上）
                    <textarea
                      defaultValue={currentDecision?.evidenceNote ?? ''}
                      minLength={10}
                      name="evidenceNote"
                      required
                      rows={4}
                    />
                  </label>
                  <div className="form-grid compact-grid">
                    <label>
                      有効開始
                      <input
                        defaultValue={currentDecision?.effectiveFrom ?? ''}
                        name="effectiveFrom"
                        type="date"
                      />
                    </label>
                    <label>
                      有効期限
                      <input
                        defaultValue={currentDecision?.validUntil ?? ''}
                        name="validUntil"
                        type="date"
                      />
                    </label>
                  </div>
                  <small>
                    「許諾済み」には有効開始日と有効期限が必要です。許諾不要確認は、根拠URLと判断根拠を必ず残してください。
                  </small>
                  <button disabled={busy}>新しい許諾判断を履歴登録</button>
                </form>

                <div className="stack source-permission-history">
                  <h4>この用途の判断履歴</h4>
                  {history.length === 0 ? (
                    <p className="muted">まだ判断履歴がありません。</p>
                  ) : (
                    history.map((decision) => (
                      <div className="source-permission-history-row" key={decision.id}>
                        <span className={`status-chip ${statusClass(decision)}`}>
                          {statusLabel(decision.effectiveStatus)}
                        </span>
                        <div>
                          <strong>{decision.evidenceNote}</strong>
                          <small>
                            {decision.decidedAt} · {decision.effectiveFrom ?? '開始日なし'}〜
                            {decision.validUntil ?? '期限なし'} ·{' '}
                            {decision.isCurrent ? '現在の判断' : '置換済み'}
                          </small>
                          <a href={decision.evidenceUrl} rel="noreferrer" target="_blank">
                            判断根拠を開く
                          </a>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
